import Layout from '../components/Layout';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface TermSection {
  id: string;
  title: string;
  content: React.ReactNode;
}

const Terms = () => {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    section1: true
  });

  const toggleSection = (sectionId: string) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const sections: TermSection[] = [
    {
      id: "section1",
      title: "1. Acceptance of Terms",
      content: (
        <div>
          <p className="mb-4">
            By inviting Fembot to your Discord server or using its features, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the bot.
          </p>
          <p>
            These Terms constitute a legally binding agreement between you and the Fembot team regarding your use of the Discord bot and any related services. By using Fembot, you represent that you are at least 13 years of age and are not barred from using the service under applicable law.
          </p>
        </div>
      )
    },
    {
      id: "section2",
      title: "2. Description of Service",
      content: (
        <div>
          <p className="mb-4">
            Fembot is a Discord bot that provides various features including moderation tools, AI capabilities, utility commands, and other interactive functions. The service is provided "as is" and may be subject to changes or interruptions.
          </p>
          <p className="mb-4">
            The Fembot team reserves the right to modify, suspend, or discontinue any aspect of the service at any time, including the availability of any feature, without prior notice or liability.
          </p>
          <p>
            While we strive to provide uninterrupted service, we cannot guarantee that the service will be available at all times. Scheduled maintenance, updates, or unforeseen technical issues may cause temporary service interruptions.
          </p>
        </div>
      )
    },
    {
      id: "section3",
      title: "3. User Conduct",
      content: (
        <div>
          <p className="mb-4">
            You agree not to use Fembot for any unlawful purposes or in any way that could damage, disable, overburden, or impair the bot or interfere with any other party's use of the bot. This includes but is not limited to:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>Attempting to gain unauthorized access to any part of the bot or its related systems</li>
            <li>Using the bot to distribute harmful content, malware, or illegal material</li>
            <li>Attempting to exploit bugs or vulnerabilities in the bot</li>
            <li>Using the bot to violate Discord's Terms of Service</li>
            <li>Engaging in any activity that disrupts or interferes with the bot's functionality</li>
            <li>Using the bot for spam, harassment, or any form of automated abuse</li>
            <li>Attempting to reverse engineer, decompile, or otherwise try to extract the source code of the bot</li>
            <li>Using the bot to collect or harvest any personally identifiable information</li>
            <li>Impersonating any person or entity, or falsely stating or otherwise misrepresenting your affiliation with a person or entity</li>
          </ul>
          <p>
            We reserve the right to investigate and take appropriate legal action against anyone who, in our sole discretion, violates this provision, including without limitation, reporting you to law enforcement authorities.
          </p>
        </div>
      )
    },
    {
      id: "section4",
      title: "4. Limitations and Restrictions",
      content: (
        <div>
          <p className="mb-4">
            The bot owner reserves the right to restrict or terminate your access to Fembot if you are found to be in violation of these terms. Additionally, the bot owner may impose limits on certain features or restrict access to parts or all of the service without notice or liability.
          </p>
          <p className="mb-4">
            We may, in our sole discretion and without prior notice, limit, suspend, or terminate your access to the bot if:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>You violate these Terms</li>
            <li>We are unable to verify or authenticate any information you provide to us</li>
            <li>We believe your actions may cause legal liability for you, other users, or us</li>
            <li>We believe you are using the bot for illicit or unauthorized purposes</li>
            <li>We believe you are using the bot in a manner that could overburden or disrupt our systems</li>
          </ul>
          <p>
            Any attempt to bypass restrictions, such as creating new accounts or using alternative methods to access the service after termination, is strictly prohibited and may result in legal action.
          </p>
        </div>
      )
    },
    {
      id: "section5",
      title: "5. Intellectual Property",
      content: (
        <div>
          <p className="mb-4">
            All content related to Fembot, including but not limited to code, text, graphics, logos, and names are the property of the bot owner and are protected by intellectual property laws. You may not reproduce, distribute, or create derivative works based on this content without explicit permission.
          </p>
          <p className="mb-4">
            The Fembot name, logo, and all related names, logos, product and service names, designs, and slogans are trademarks of the Fembot team or its affiliates. You may not use such marks without the prior written permission of the Fembot team.
          </p>
          <p className="mb-4">
            All other names, brands, and marks are used for identification purposes only and may be the trademarks of their respective owners.
          </p>
          <p>
            Any feedback, comments, or suggestions you may provide regarding Fembot is entirely voluntary, and we will be free to use such feedback, comments, or suggestions without any obligation to you.
          </p>
        </div>
      )
    },
    {
      id: "section6",
      title: "6. Premium Services and Payment",
      content: (
        <div>
          <p className="mb-4">
            Fembot offers premium subscription tiers that provide enhanced features and capabilities beyond the free version. By subscribing to a premium plan, you agree to the following:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>
              <strong>Billing:</strong> You will be billed in advance on a recurring basis depending on the subscription plan you select (monthly or yearly). You authorize us to charge the payment method you provide for the subscription fees and any applicable taxes.
            </li>
            <li>
              <strong>Automatic Renewal:</strong> Your subscription will automatically renew at the end of each billing period unless you cancel it before the renewal date.
            </li>
            <li>
              <strong>Cancellation:</strong> You may cancel your subscription at any time. Upon cancellation, you will continue to have access to premium features until the end of your current billing period, but you will not receive a refund for the current billing period.
            </li>
            <li>
              <strong>Price Changes:</strong> We reserve the right to adjust pricing for our services at any time. If the subscription fee changes, we will notify you before charging the new rate.
            </li>
            <li>
              <strong>Refunds:</strong> Refunds are handled on a case-by-case basis and are not guaranteed. If you believe you are entitled to a refund, please contact our support team.
            </li>
          </ul>
          <p className="mb-4">
            Payment processing is handled by third-party payment processors, and your use of their services is subject to their terms of service and privacy policies. We do not store complete credit card information on our servers.
          </p>
          <p>
            Premium features are subject to the same usage limitations and conduct requirements as the free service, as outlined in these Terms.
          </p>
        </div>
      )
    },
    {
      id: "section7",
      title: "7. Disclaimer of Warranty",
      content: (
        <div>
          <p className="mb-4">
            Fembot is provided "as is" without warranty of any kind, either express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement. The bot owner does not warrant that the service will be uninterrupted or error-free.
          </p>
          <p className="mb-4">
            We make no warranty that:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>The service will meet your specific requirements</li>
            <li>The service will be uninterrupted, timely, secure, or error-free</li>
            <li>The results that may be obtained from the use of the service will be accurate or reliable</li>
            <li>Any errors in the service will be corrected</li>
          </ul>
          <p className="mb-4">
            You understand and agree that you use the service at your own discretion and risk, and that you will be solely responsible for any damage to your computer system or loss of data that results from the use of the service.
          </p>
          <p>
            Some jurisdictions do not allow the exclusion of certain warranties, so some of the above exclusions may not apply to you.
          </p>
        </div>
      )
    },
    {
      id: "section8",
      title: "8. Limitation of Liability",
      content: (
        <div>
          <p className="mb-4">
            In no event shall the bot owner be liable for any direct, indirect, incidental, special, or consequential damages arising out of or in connection with the use or inability to use Fembot, even if advised of the possibility of such damages.
          </p>
          <p className="mb-4">
            To the maximum extent permitted by applicable law, the Fembot team, its officers, directors, employees, and agents shall not be liable for:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>Any indirect, punitive, incidental, special, consequential, or exemplary damages, including without limitation damages for loss of profits, goodwill, use, data, or other intangible losses</li>
            <li>Any damage, loss, or injury resulting from hacking, tampering, or other unauthorized access or use of the service or your account</li>
            <li>Any content or information you submit while using the service</li>
            <li>Any errors or omissions in the operation of the service</li>
            <li>Any interruption, suspension, or termination of the service</li>
          </ul>
          <p>
            The limitations of damages set forth above are fundamental elements of the basis of the bargain between the Fembot team and you. Some jurisdictions do not allow the exclusion or limitation of liability for consequential or incidental damages, so the above limitation may not apply to you.
          </p>
        </div>
      )
    },
    {
      id: "section9",
      title: "9. Changes to Terms",
      content: (
        <div>
          <p className="mb-4">
            The bot owner reserves the right to modify these Terms of Service at any time. Continued use of Fembot after such modifications constitutes your acceptance of the revised terms.
          </p>
          <p className="mb-4">
            We will make reasonable efforts to notify users of any significant changes by posting a notice on our website, through the bot itself, or through our support server. However, it is your responsibility to review these Terms periodically for changes.
          </p>
          <p>
            If you do not agree to the modified terms, you should discontinue your use of Fembot. Your continued use of the service after the effective date of any changes constitutes your agreement to the modified terms.
          </p>
        </div>
      )
    },
    {
      id: "section10",
      title: "10. Governing Law",
      content: (
        <div>
          <p className="mb-4">
            These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which the bot owner operates, without regard to its conflict of law provisions.
          </p>
          <p className="mb-4">
            You agree that any legal action or proceeding between you and the Fembot team shall be brought exclusively in the courts located in the jurisdiction where the bot owner operates. You hereby agree to submit to the personal jurisdiction of such courts.
          </p>
          <p>
            The application of the United Nations Convention on Contracts for the International Sale of Goods is expressly excluded.
          </p>
        </div>
      )
    },
    {
      id: "section11",
      title: "11. Indemnification",
      content: (
        <div>
          <p className="mb-4">
            You agree to defend, indemnify, and hold harmless the Fembot team, its officers, directors, employees and agents, from and against any and all claims, damages, obligations, losses, liabilities, costs or debt, and expenses (including but not limited to attorney's fees) arising from:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>Your use of and access to Fembot</li>
            <li>Your violation of any term of these Terms</li>
            <li>Your violation of any third-party right, including without limitation any copyright, property, or privacy right</li>
            <li>Any claim that content you submitted caused damage to a third party</li>
          </ul>
          <p>
            This defense and indemnification obligation will survive these Terms and your use of Fembot.
          </p>
        </div>
      )
    },
    {
      id: "section12",
      title: "12. Severability and Waiver",
      content: (
        <div>
          <p className="mb-4">
            If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited or eliminated to the minimum extent necessary so that the Terms will otherwise remain in full force and effect and enforceable.
          </p>
          <p className="mb-4">
            The failure of the Fembot team to exercise or enforce any right or provision of these Terms shall not constitute a waiver of such right or provision.
          </p>
          <p>
            No waiver by the Fembot team of any term or condition set out in these Terms shall be deemed a further or continuing waiver of such term or condition or a waiver of any other term or condition, and any failure to assert a right or provision under these Terms shall not constitute a waiver of such right or provision.
          </p>
        </div>
      )
    },
    {
      id: "section13",
      title: "13. API Usage and Rate Limits",
      content: (
        <div>
          <p className="mb-4">
            Fembot interacts with various APIs, including Discord's API and third-party services. The bot implements rate limiting to ensure fair usage and compliance with these services' terms.
          </p>
          <p className="mb-4">
            Users agree not to:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>Attempt to circumvent any rate limits or restrictions imposed by Fembot</li>
            <li>Use automated scripts or tools to interact with the bot in ways that could overload our systems</li>
            <li>Perform actions that could cause the bot to exceed its own rate limits with external APIs</li>
            <li>Use the bot in a manner that violates the terms of service of any third-party API that the bot interacts with</li>
          </ul>
          <p className="mb-4">
            The Fembot team reserves the right to temporarily or permanently restrict access to specific commands or features if abuse is detected. In severe cases of API abuse, servers may be blacklisted from using the bot entirely.
          </p>
        </div>
      )
    },
    {
      id: "section14",
      title: "14. Dispute Resolution",
      content: (
        <div>
          <p className="mb-4">
            In the event of any dispute, controversy, or claim arising out of or relating to these Terms or the breach, termination, or validity thereof (a "Dispute"), the parties shall attempt in good faith to resolve such Dispute through informal means.
          </p>
          <p className="mb-4">
            If the Dispute is not resolved within 30 days from the date the Dispute arose, either party may initiate formal proceedings. Any formal legal action must be initiated in the courts of the jurisdiction where the Fembot team operates, and the parties consent to the exclusive jurisdiction of such courts.
          </p>
          <p className="mb-4">
            You agree to resolve any claims relating to these Terms or our services on an individual basis, and expressly waive any right to participate in a class action lawsuit or class-wide arbitration.
          </p>
        </div>
      )
    },
    {
      id: "section15",
      title: "15. Use of AI-Generated Content",
      content: (
        <div>
          <p className="mb-4">
            Certain features of Fembot may generate AI content in response to user requests. You acknowledge and agree that:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>AI-generated content may not always be accurate, appropriate, or aligned with your expectations</li>
            <li>You are solely responsible for how you use AI-generated content</li>
            <li>The Fembot team is not responsible for any consequences resulting from your use of AI-generated content</li>
            <li>Content generated by AI features should be reviewed and verified before being used for critical or public-facing purposes</li>
          </ul>
          <p className="mb-4">
            The AI features in Fembot are provided to enhance user experience, but you should exercise appropriate judgment when using and relying on such content.
          </p>
          <p>
            The Fembot team reserves the right to monitor AI usage to prevent abuse, improve the quality of responses, and ensure compliance with our content guidelines.
          </p>
        </div>
      )
    },
    {
      id: "section16",
      title: "16. Contact Information",
      content: (
        <div>
          <p className="mb-4">
            If you have any questions about these Terms, please contact us through our support server or via email at lunaxxdev@gmail.com.
          </p>
          <p className="mb-4">
            For customer support inquiries, feature requests, or to report abuse, please join our support server or reach out via the contact methods provided on our website.
          </p>
          <p className="mb-4">
            Support Server: <a href="https://discord.gg/YjtsNfm2Uk" target="_blank" rel="noopener noreferrer" className="text-fembot-pink hover:underline">Join our Discord</a>
          </p>
          <p>
            We welcome your feedback and suggestions on how to improve our service. Your input helps us make Fembot better for everyone.
          </p>
        </div>
      )
    }
  ];

  return (
    <Layout>
      <div className="container-custom py-16 md:py-24">
        <h1 className="text-3xl md:text-4xl font-bold mb-8 dark:text-white">Terms of Service</h1>
        
        <div className="prose prose-lg dark:prose-invert max-w-none">
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
            Last updated: April 15, 2025
          </p>
          
          <div className="space-y-6">
            {sections.map((section) => (
              <Collapsible 
                key={section.id} 
                open={openSections[section.id]} 
                onOpenChange={() => toggleSection(section.id)}
                className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
              >
                <CollapsibleTrigger className="w-full px-6 py-4 flex justify-between items-center bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  <h2 className="text-xl font-semibold dark:text-white">{section.title}</h2>
                  {openSections[section.id] ? (
                    <ChevronUp className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                  )}
                </CollapsibleTrigger>
                <CollapsibleContent className="px-6 py-4 bg-white dark:bg-gray-900">
                  <div className="text-gray-700 dark:text-gray-300">
                    {section.content}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Terms;
